import { Link } from 'react-router-dom';
import { useState, useEffect } from 'react';
import Header from '../../components/Header';
import Footer from '../../components/Footer';
import SEO from '../../components/SEO';
import FAQSection from './components/FAQSection';

export default function HomePage() {
  const [isScrolled, setIsScrolled] = useState(false);
  const siteUrl = import.meta.env.VITE_SITE_URL || 'https://example.com';

  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 50);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const jsonLd = [
    {
      '@context': 'https://schema.org',
      '@type': 'LocalBusiness',
      '@id': `${siteUrl}/#organization`,
      name: 'IGM Facility Service',
      image: `${siteUrl}/og-image.jpg`,
      description: 'Professionelle Gebäudereinigung, Hausmeisterservice und Schlüsseldienst in Ratingen. Zuverlässiger Partner für Gebäudeservice mit über 10 Jahren Erfahrung.',
      address: {
        '@type': 'PostalAddress',
        streetAddress: 'Industriestr. 37',
        addressLocality: 'Ratingen',
        postalCode: '40878',
        addressCountry: 'DE',
      },
      geo: {
        '@type': 'GeoCoordinates',
        latitude: 51.2966,
        longitude: 6.8499,
      },
      url: siteUrl,
      telephone: '+492102739807',
      email: 'info@igmfacilityservice.de',
      priceRange: '$$',
      openingHoursSpecification: [
        {
          '@type': 'OpeningHoursSpecification',
          dayOfWeek: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday'],
          opens: '10:00',
          closes: '15:00',
        },
        {
          '@type': 'OpeningHoursSpecification',
          dayOfWeek: 'Saturday',
          opens: '12:00',
          closes: '15:00',
        },
      ],
      aggregateRating: {
        '@type': 'AggregateRating',
        ratingValue: '9.8',
        bestRating: '10',
        ratingCount: '500',
      },
      areaServed: [
        {
          '@type': 'City',
          name: 'Ratingen',
        },
        {
          '@type': 'City',
          name: 'Düsseldorf',
        },
        {
          '@type': 'City',
          name: 'Mettmann',
        },
        {
          '@type': 'City',
          name: 'Erkrath',
        },
        {
          '@type': 'City',
          name: 'Hilden',
        },
        {
          '@type': 'City',
          name: 'Langenfeld',
        },
        {
          '@type': 'City',
          name: 'Velbert',
        },
        {
          '@type': 'City',
          name: 'Heiligenhaus',
        },
        {
          '@type': 'City',
          name: 'Wülfrath',
        },
        {
          '@type': 'City',
          name: 'Neuss',
        },
        {
          '@type': 'City',
          name: 'Krefeld',
        },
      ],
      hasOfferCatalog: {
        '@type': 'OfferCatalog',
        name: 'Facility Services',
        itemListElement: [
          {
            '@type': 'Offer',
            itemOffered: {
              '@type': 'Service',
              name: 'Gebäudereinigung Ratingen',
              description: 'Professionelle Unterhalts-, Grund- und Büroreinigung',
            },
          },
          {
            '@type': 'Offer',
            itemOffered: {
              '@type': 'Service',
              name: 'Hausmeisterservice Ratingen',
              description: 'Zuverlässige Wartung, Reparaturen und Grünpflege',
            },
          },
          {
            '@type': 'Offer',
            itemOffered: {
              '@type': 'Service',
              name: 'Schlüsseldienst Ratingen',
              description: '24/7 Notöffnung und Schlossaustausch',
            },
          },
        ],
      },
    },
    {
      '@context': 'https://schema.org',
      '@type': 'WebSite',
      '@id': `${siteUrl}/#website`,
      url: siteUrl,
      name: 'IGM Facility Service',
      description: 'Professioneller Gebäudeservice in Ratingen',
      publisher: {
        '@id': `${siteUrl}/#organization`,
      },
    },
  ];

  return (
    <div className="min-h-screen bg-white overflow-x-hidden">
      <SEO
        title="Gebäudereinigung Ratingen | Hausmeisterservice | Schlüsseldienst | IGM Facility Service"
        description="IGM Facility Service Ratingen: Professionelle Gebäudereinigung, zuverlässiger Hausmeisterservice und 24/7 Schlüsseldienst. Über 10 Jahre Erfahrung. Jetzt Angebot anfragen!"
        keywords="Gebäudereinigung Ratingen, Hausmeisterservice Ratingen, Schlüsseldienst Ratingen, Facility Service, Büroreinigung, Winterdienst, Fensterreinigung"
        canonical="/"
        jsonLd={jsonLd}
      />
      <Header />

      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center justify-center overflow-hidden">
        <div className="absolute inset-0 bg-cover bg-center bg-no-repeat" style={{backgroundImage: 'url(https://public.readdy.ai/ai/img_res/781fec53eba4c4fb8a7066c21c4d5868.jpg)'}}>
        </div>
        <div className="absolute inset-0 bg-gradient-to-r from-black/65 via-black/45 to-black/25"></div>
        <div className="relative z-10 w-full container mx-auto px-5 sm:px-6 pt-28 pb-16 sm:pt-32 sm:pb-20">
          <div className="max-w-4xl">
            <h1 className="text-2xl sm:text-4xl md:text-5xl lg:text-7xl font-bold text-white mb-4 sm:mb-6 leading-tight drop-shadow-lg" style={{textShadow: '0 2px 12px rgba(0,0,0,0.5)'}}>
              Ihr zuverlässiger Partner für Gebäudeservice
            </h1>
            <p className="text-sm sm:text-lg md:text-xl lg:text-2xl text-white/90 mb-7 sm:mb-10 leading-relaxed drop-shadow-md max-w-2xl" style={{textShadow: '0 1px 8px rgba(0,0,0,0.5)'}}>
              Professionelle Reinigung, Hausmeisterservice und Schlüsseldienst – alles aus einer Hand.
            </p>
            <div className="flex flex-col sm:flex-row gap-3 sm:gap-4">
              <Link to="/kontakt" className="inline-flex items-center justify-center gap-2 bg-[#0D2B4E] text-white px-6 py-3.5 sm:px-8 sm:py-4 rounded-lg text-sm sm:text-lg font-semibold hover:bg-[#0a2240] transition-all shadow-lg whitespace-nowrap cursor-pointer">
                <span>Angebot anfragen</span>
                <i className="ri-arrow-right-line text-lg sm:text-xl"></i>
              </Link>
              <Link to="/leistungen" className="inline-flex items-center justify-center gap-2 bg-white/10 backdrop-blur-sm text-white border-2 border-white px-6 py-3.5 sm:px-8 sm:py-4 rounded-lg text-sm sm:text-lg font-semibold hover:bg-white/20 transition-all whitespace-nowrap cursor-pointer">
                <span>Leistungen entdecken</span>
                <i className="ri-service-line text-lg sm:text-xl"></i>
              </Link>
            </div>
          </div>
        </div>
        <div className="absolute bottom-6 sm:bottom-8 left-1/2 -translate-x-1/2 animate-bounce hidden sm:block">
          <i className="ri-arrow-down-line text-white text-3xl"></i>
        </div>
      </section>

      {/* Trust Bar Section */}
      <section className="py-12 sm:py-16 bg-gray-50">
        <div className="container mx-auto px-4 sm:px-6">
          <div className="text-center mb-8 sm:mb-12">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-gray-900 mb-3 sm:mb-4">Warum wir?</h2>
            <p className="text-base sm:text-lg text-gray-600">Vertrauen Sie auf unsere Erfahrung und Qualität</p>
          </div>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-8">
            {[
              { icon: 'ri-time-line', value: '10+', label: 'Jahre Erfahrung' },
              { icon: 'ri-team-line', value: '500+', label: 'Zufriedene Kunden' },
              { icon: 'ri-24-hours-line', value: '24/7', label: 'Notdienst verfügbar' },
              { icon: 'ri-star-line', value: '100%', label: 'Zufriedenheit' },
            ].map((item, i) => (
              <div key={i} className="bg-white rounded-xl p-4 sm:p-8 text-center shadow-md hover:shadow-xl transition-shadow">
                <div className="w-14 h-14 sm:w-20 sm:h-20 bg-[#0D2B4E]/10 rounded-full flex items-center justify-center mx-auto mb-3 sm:mb-6">
                  <i className={`${item.icon} text-2xl sm:text-4xl text-[#0D2B4E]`}></i>
                </div>
                <h3 className="text-2xl sm:text-4xl font-bold text-[#0D2B4E] mb-1 sm:mb-2">{item.value}</h3>
                <p className="text-xs sm:text-base text-gray-700 font-medium">{item.label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Services Overview Section */}
      <section className="py-12 sm:py-20 bg-white">
        <div className="container mx-auto px-4 sm:px-6">
          <div className="text-center mb-10 sm:mb-16">
            <h2 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold text-gray-900 mb-3 sm:mb-4">Unsere Leistungen</h2>
            <p className="text-base sm:text-lg md:text-xl text-gray-600 max-w-3xl mx-auto px-4">
              Von der professionellen Gebäudereinigung bis zum 24/7-Notdienst – wir bieten Ihnen umfassende Dienstleistungen für Ihre Immobilie
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 sm:gap-8">
            {[
              { img: 'https://readdy.ai/api/search-image?query=professional%20commercial%20office%20cleaning%20service%20with%20modern%20equipment%20showing%20spotless%20clean%20floors%20and%20workspace%20in%20bright%20well%20lit%20environment%20emphasizing%20hygiene%20and%20professionalism%20with%20simple%20clean%20background&width=600&height=400&seq=service-cleaning-001&orientation=landscape', icon: 'ri-brush-line', title: 'Gebäudereinigung', desc: 'Professionelle Unterhalts-, Grund- und Büroreinigung für makellose Sauberkeit in Ihren Räumlichkeiten.', link: '/leistungen' },
              { img: 'https://readdy.ai/api/search-image?query=professional%20building%20maintenance%20technician%20performing%20repair%20work%20in%20modern%20facility%20with%20tools%20and%20equipment%20showing%20expertise%20and%20reliability%20in%20bright%20clean%20environment%20with%20simple%20background&width=600&height=400&seq=service-hausmeister-001&orientation=landscape', icon: 'ri-tools-line', title: 'Hausmeisterservice', desc: 'Zuverlässige Wartung, Reparaturen, Grünpflege und Winterdienst für Ihre Immobilie.', link: '/hausmeister' },
              { img: 'https://readdy.ai/api/search-image?query=professional%20locksmith%20service%20technician%20working%20on%20modern%20door%20lock%20installation%20with%20specialized%20tools%20showing%20security%20and%20expertise%20in%20clean%20bright%20environment%20with%20simple%20background&width=600&height=400&seq=service-locksmith-001&orientation=landscape', icon: 'ri-key-line', title: 'Schlüsseldienst', desc: '24/7 Notöffnung, Schlossaustausch und Sicherheitsberatung – schnell und zuverlässig.', link: '/schluesseldienst' },
              { img: 'https://readdy.ai/api/search-image?query=professional%20window%20cleaning%20service%20on%20modern%20glass%20building%20facade%20with%20clean%20streak%20free%20windows%20reflecting%20blue%20sky%20showing%20precision%20and%20quality%20work%20in%20bright%20daylight%20with%20simple%20background&width=600&height=400&seq=service-window-001&orientation=landscape', icon: 'ri-window-line', title: 'Fensterreinigung', desc: 'Streifenfreie Glas- und Fensterreinigung für klaren Durchblick in Ihren Räumen.', link: '/leistungen' },
              { img: 'https://readdy.ai/api/search-image?query=professional%20winter%20snow%20removal%20service%20clearing%20pathway%20and%20entrance%20with%20modern%20equipment%20showing%20efficiency%20and%20safety%20in%20snowy%20environment%20with%20clean%20cleared%20areas%20and%20simple%20background&width=600&height=400&seq=service-winter-001&orientation=landscape', icon: 'ri-snowy-line', title: 'Winterdienst', desc: 'Zuverlässiger Räum- und Streudienst für sichere Wege und Zufahrten im Winter.', link: '/leistungen' },
              { img: 'https://readdy.ai/api/search-image?query=professional%20moving%20out%20cleaning%20service%20in%20empty%20apartment%20with%20spotless%20clean%20rooms%20and%20floors%20showing%20thorough%20deep%20cleaning%20work%20in%20bright%20well%20lit%20space%20with%20simple%20clean%20background&width=600&height=400&seq=service-moving-001&orientation=landscape', icon: 'ri-home-smile-line', title: 'Umzugsreinigung', desc: 'Gründliche End- und Endreinigung für einen reibungslosen Wohnungswechsel.', link: '/leistungen' },
            ].map((s, i) => (
              <div key={i} className="group bg-white rounded-xl shadow-lg overflow-hidden hover:shadow-2xl transition-all duration-300 cursor-pointer">
                <div className="relative h-48 sm:h-64 overflow-hidden">
                  <img src={s.img} alt={s.title} className="w-full h-full object-cover object-top group-hover:scale-110 transition-transform duration-500" />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent"></div>
                  <div className="absolute bottom-3 left-3 sm:bottom-4 sm:left-4">
                    <div className="w-12 h-12 sm:w-14 sm:h-14 bg-[#0D2B4E] rounded-lg flex items-center justify-center">
                      <i className={`${s.icon} text-xl sm:text-2xl text-white`}></i>
                    </div>
                  </div>
                </div>
                <div className="p-4 sm:p-6">
                  <h3 className="text-xl sm:text-2xl font-bold text-gray-900 mb-2 sm:mb-3">{s.title}</h3>
                  <p className="text-gray-600 mb-3 sm:mb-4 text-sm leading-relaxed">{s.desc}</p>
                  <Link to={s.link} className="inline-flex items-center gap-2 text-[#0D2B4E] font-semibold hover:gap-3 transition-all cursor-pointer">
                    <span>Mehr erfahren</span>
                    <i className="ri-arrow-right-line"></i>
                  </Link>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* About Us Section */}
      <section className="py-12 sm:py-20 bg-gray-50">
        <div className="container mx-auto px-4 sm:px-6">
          <div className="grid lg:grid-cols-2 gap-8 sm:gap-12 items-center">
            <div className="order-2 lg:order-1">
              <h2 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold text-gray-900 mb-4 sm:mb-6">Über uns</h2>
              <p className="text-base sm:text-lg text-gray-600 mb-4 sm:mb-6 leading-relaxed">
                Mit über 10 Jahren Erfahrung ist <strong>IGM Facility Service</strong> Ihr verlässlicher Partner für professionelle Gebäudedienstleistungen in Ratingen und Umgebung.
              </p>
              <p className="text-base sm:text-lg text-gray-600 mb-4 sm:mb-6 leading-relaxed">
                Ob privat oder gewerblich – makellose Sauberkeit und Gründlichkeit bis ins Detail zeichnen unsere Arbeit aus.
              </p>
              <div className="bg-[#0D2B4E]/5 border-l-4 border-[#0D2B4E] rounded-lg p-3 sm:p-4 mb-6 sm:mb-8">
                <div className="flex items-start gap-2 sm:gap-3">
                  <div className="w-6 h-6 sm:w-8 sm:h-8 flex items-center justify-center flex-shrink-0 mt-0.5">
                    <i className="ri-information-line text-lg sm:text-xl text-[#0D2B4E]"></i>
                  </div>
                  <p className="text-gray-700 text-xs sm:text-sm leading-relaxed">
                    <strong>Meilenstein 2024:</strong> IGM Facility Service ist aus der Fusion mit <strong>Amaterasu Facility</strong> hervorgegangen. Durch diesen Zusammenschluss bündeln wir unsere Stärken und bieten Ihnen ein noch breiteres Leistungsspektrum mit gewachsener Erfahrung.
                  </p>
                </div>
              </div>
              <div className="space-y-3 sm:space-y-4 mb-6 sm:mb-8">
                {[
                  { label: 'Qualität', text: 'Höchste Standards bei allen Dienstleistungen' },
                  { label: 'Zuverlässigkeit', text: 'Pünktlich und verlässlich zu jeder Zeit' },
                  { label: 'Nachhaltigkeit', text: 'Umweltfreundliche Reinigungsmethoden' },
                  { label: 'Flexibilität', text: 'Individuelle Lösungen für Ihre Bedürfnisse' },
                ].map((item, i) => (
                  <div key={i} className="flex items-start gap-2 sm:gap-3">
                    <div className="w-5 h-5 sm:w-6 sm:h-6 bg-[#0D2B4E] rounded-full flex items-center justify-center flex-shrink-0 mt-0.5 sm:mt-1">
                      <i className="ri-check-line text-white text-xs sm:text-sm"></i>
                    </div>
                    <p className="text-sm sm:text-base text-gray-700"><strong>{item.label}:</strong> {item.text}</p>
                  </div>
                ))}
              </div>
              <Link to="/kontakt" className="inline-flex items-center gap-2 bg-[#0D2B4E] text-white px-6 sm:px-8 py-3 sm:py-4 rounded-lg text-base sm:text-lg font-semibold hover:bg-[#0a2240] transition-colors whitespace-nowrap cursor-pointer w-full sm:w-auto justify-center">
                <span>Jetzt kontaktieren</span>
                <i className="ri-arrow-right-line"></i>
              </Link>
            </div>
            <div className="order-1 lg:order-2">
              <div className="relative">
                <div className="aspect-[4/3] rounded-2xl overflow-hidden shadow-2xl">
                  <img src="https://static.readdy.ai/image/42a59d03aabdb3bbb4adf980fce8c22a/06c5a492ce4163d31f2cea52f8cee880.jpeg" alt="Unser Team" className="w-full h-full object-cover object-top" />
                </div>
                <div className="absolute -bottom-4 -right-4 sm:-bottom-6 sm:-right-6 w-32 h-32 sm:w-48 sm:h-48 bg-[#0D2B4E] rounded-2xl -z-10"></div>
                <div className="absolute -top-4 -left-4 sm:-top-6 sm:-left-6 w-32 h-32 sm:w-48 sm:h-48 bg-[#0D2B4E]/10 rounded-2xl -z-10"></div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section className="py-12 sm:py-20 bg-white">
        <div className="container mx-auto px-4 sm:px-6">
          <div className="text-center mb-10 sm:mb-16">
            <div className="inline-flex items-center gap-2 bg-[#0D2B4E]/5 rounded-full px-4 sm:px-5 py-2 mb-4 sm:mb-5">
              <i className="ri-medal-line text-[#0D2B4E] text-base sm:text-lg"></i>
              <span className="text-xs sm:text-sm font-semibold text-[#0D2B4E] tracking-wide">Verifizierte TrustLocal-Bewertungen</span>
            </div>
            <h2 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold text-gray-900 mb-3 sm:mb-4">Was unsere Kunden sagen</h2>
            <p className="text-base sm:text-lg md:text-xl text-gray-600">Echte Erfahrungen – direkt von TrustLocal</p>
            <div className="flex items-center justify-center gap-2 sm:gap-3 mt-4 sm:mt-6">
              <div className="flex gap-0.5 sm:gap-1">
                {[...Array(5)].map((_, i) => <i key={i} className="ri-star-fill text-yellow-400 text-lg sm:text-2xl"></i>)}
              </div>
              <span className="text-2xl sm:text-3xl font-bold text-gray-900">9,8</span>
              <span className="text-gray-500 text-xs sm:text-sm">/ 10 auf TrustLocal</span>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
            {[
              {
                initials: 'KH',
                name: 'Klaus H.',
                role: 'Gebäudereinigung',
                text: 'Sehr professionelles Team! Die Büroreinigung wird seit Monaten pünktlich und gründlich durchgeführt. Man merkt, dass hier mit Herzblut gearbeitet wird. Absolute Weiterempfehlung!',
                stars: 5,
                date: 'November 2024',
              },
              {
                initials: 'SR',
                name: 'Sabine R.',
                role: 'Hausmeisterservice',
                text: 'Schnelle Reaktionszeit und faire Preise. Unser Hausmeisterproblem wurde innerhalb eines Tages gelöst. Sehr freundlicher Kontakt und saubere Arbeit – wir sind rundum zufrieden.',
                stars: 5,
                date: 'Oktober 2024',
              },
              {
                initials: 'MF',
                name: 'Marco F.',
                role: 'Schlüsseldienst Notöffnung',
                text: 'Mitten in der Nacht ausgesperrt – IGM war in unter 30 Minuten da! Kein Aufpreis für den Nachtdienst, transparente Abrechnung. Genau so stellt man sich einen Notdienst vor.',
                stars: 5,
                date: 'September 2024',
              },
              {
                initials: 'BL',
                name: 'Brigitte L.',
                role: 'Umzugsreinigung',
                text: 'Die Endreinigung nach unserem Auszug war makellos. Die Wohnung war danach sauberer als beim Einzug. Kaution wurde vollständig zurückerstattet. Vielen Dank an das ganze Team!',
                stars: 5,
                date: 'August 2024',
              },
              {
                initials: 'JP',
                name: 'Jürgen P.',
                role: 'Winterdienst',
                text: 'Zuverlässiger Winterdienst, der wirklich funktioniert. Auch bei starkem Schneefall waren die Wege immer geräumt. Kein einziger Ausfall in der gesamten Saison. Top Leistung!',
                stars: 5,
                date: 'Februar 2024',
              },
              {
                initials: 'NM',
                name: 'Nina M.',
                role: 'Fensterreinigung',
                text: 'Streifenfreie Fenster, pünktlicher Termin und ein sehr nettes Team. Der Preis war absolut fair. Ich habe IGM bereits mehreren Freunden empfohlen – alle sind begeistert.',
                stars: 5,
                date: 'Juli 2024',
              },
            ].map((t, i) => (
              <div key={i} className="bg-gray-50 rounded-2xl p-5 sm:p-7 shadow-sm hover:shadow-lg transition-all duration-300 flex flex-col justify-between border border-gray-100">
                <div>
                  <div className="flex items-center justify-between mb-3 sm:mb-4">
                    <div className="flex gap-0.5">
                      {[...Array(t.stars)].map((_, j) => <i key={j} className="ri-star-fill text-yellow-400 text-base sm:text-lg"></i>)}
                    </div>
                    <span className="text-xs text-gray-400">{t.date}</span>
                  </div>
                  <p className="text-gray-700 leading-relaxed italic text-xs sm:text-sm mb-4 sm:mb-6">„{t.text}"</p>
                </div>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2 sm:gap-3">
                    <div className="w-10 h-10 sm:w-11 sm:h-11 bg-[#0D2B4E] rounded-full flex items-center justify-center text-white font-bold text-xs sm:text-sm flex-shrink-0">{t.initials}</div>
                    <div>
                      <p className="font-semibold text-gray-900 text-xs sm:text-sm">{t.name}</p>
                      <p className="text-xs text-gray-500">{t.role}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-1 bg-white border border-gray-200 rounded-full px-2 sm:px-3 py-1">
                    <i className="ri-shield-check-line text-green-500 text-xs sm:text-sm"></i>
                    <span className="text-xs text-gray-500 font-medium hidden sm:inline">TrustLocal</span>
                  </div>
                </div>
              </div>
            ))}
          </div>

          <div className="text-center mt-8 sm:mt-12">
            <a
              href="https://trustlocal.de/r/6d29206952"
              target="_blank"
              rel="nofollow noopener noreferrer"
              className="inline-flex items-center gap-2 border-2 border-[#0D2B4E] text-[#0D2B4E] px-5 sm:px-7 py-3 rounded-xl font-semibold hover:bg-[#0D2B4E] hover:text-white transition-all cursor-pointer whitespace-nowrap text-sm sm:text-base"
            >
              <i className="ri-external-link-line"></i>
              <span>Alle Bewertungen auf TrustLocal ansehen</span>
            </a>
          </div>
        </div>
      </section>

      {/* TrustLocal Bewertungs-Section */}
      <section className="py-12 sm:py-20 bg-gray-50">
        <div className="container mx-auto px-4 sm:px-6">
          <div className="max-w-3xl mx-auto text-center">
            <div className="w-16 h-16 sm:w-20 sm:h-20 bg-white rounded-full shadow-lg flex items-center justify-center mx-auto mb-4 sm:mb-6">
              <i className="ri-medal-line text-3xl sm:text-4xl text-[#0D2B4E]"></i>
            </div>
            <h2 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold text-gray-900 mb-3 sm:mb-4">Waren Sie zufrieden?</h2>
            <p className="text-base sm:text-lg md:text-xl text-gray-600 mb-3 sm:mb-4 leading-relaxed px-4">
              Ihre Meinung ist uns wichtig! Helfen Sie anderen Kunden, uns zu finden – hinterlassen Sie eine Bewertung auf TrustLocal.
            </p>
            <div className="flex justify-center gap-0.5 sm:gap-1 mb-6 sm:mb-8">
              {[...Array(5)].map((_, i) => (
                <i key={i} className="ri-star-fill text-yellow-400 text-2xl sm:text-3xl"></i>
              ))}
            </div>
            <div className="bg-white rounded-2xl shadow-xl p-5 sm:p-8 mb-6 sm:mb-8">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 sm:gap-6 mb-6 sm:mb-8">
                {[
                  { value: '9,8', label: 'Bewertungsdurchschnitt', icon: 'ri-star-fill', color: 'text-yellow-400' },
                  { value: '5,4 Mio.', label: 'TrustLocal-Nutzer', icon: 'ri-group-line', color: 'text-[#0D2B4E]' },
                  { value: '98%', label: 'Weiterempfehlungsrate', icon: 'ri-thumb-up-line', color: 'text-green-500' },
                ].map((stat, i) => (
                  <div key={i} className="text-center p-3 sm:p-4 rounded-xl bg-gray-50">
                    <div className="w-10 h-10 sm:w-12 sm:h-12 flex items-center justify-center mx-auto mb-2 sm:mb-3">
                      <i className={`${stat.icon} text-2xl sm:text-3xl ${stat.color}`}></i>
                    </div>
                    <p className="text-2xl sm:text-3xl font-bold text-gray-900 mb-1">{stat.value}</p>
                    <p className="text-xs sm:text-sm text-gray-600">{stat.label}</p>
                  </div>
                ))}
              </div>
              <div className="flex flex-col sm:flex-row items-center justify-center gap-2 mb-4 sm:mb-6">
                <div className="flex items-center gap-1 bg-[#0D2B4E]/5 rounded-full px-3 sm:px-4 py-2">
                  <i className="ri-shield-check-line text-[#0D2B4E] text-base sm:text-lg"></i>
                  <span className="text-xs sm:text-sm font-medium text-[#0D2B4E]">Top Preis-Leistungsverhältnis</span>
                </div>
                <div className="flex items-center gap-1 bg-green-50 rounded-full px-3 sm:px-4 py-2">
                  <i className="ri-flashlight-line text-green-600 text-base sm:text-lg"></i>
                  <span className="text-xs sm:text-sm font-medium text-green-700">Reagiert schnell</span>
                </div>
              </div>
              <a
                href="https://trustlocal.de/r/6d29206952"
                target="_blank"
                rel="nofollow noopener noreferrer"
                className="inline-flex items-center justify-center gap-2 sm:gap-3 bg-[#0D2B4E] text-white px-6 sm:px-10 py-3 sm:py-4 rounded-xl text-base sm:text-lg font-semibold hover:bg-[#0a2240] transition-all shadow-lg hover:shadow-xl hover:-translate-y-0.5 whitespace-nowrap cursor-pointer w-full sm:w-auto"
              >
                <i className="ri-edit-line text-xl sm:text-2xl"></i>
                <span>Jetzt bei TrustLocal bewerten</span>
                <i className="ri-external-link-line text-lg sm:text-xl"></i>
              </a>
              <p className="text-xs sm:text-sm text-gray-400 mt-3 sm:mt-4">Dauert nur 1 Minute – vielen Dank für Ihr Vertrauen!</p>
            </div>
          </div>
        </div>
      </section>

      <FAQSection />

      {/* Service Area Section */}
      <section className="py-12 sm:py-20 bg-white">
        <div className="container mx-auto px-4 sm:px-6">
          <div className="text-center mb-10 sm:mb-16">
            <div className="inline-flex items-center gap-2 bg-[#0D2B4E]/5 rounded-full px-4 sm:px-5 py-2 mb-4 sm:mb-5">
              <i className="ri-map-pin-line text-[#0D2B4E] text-base sm:text-lg"></i>
              <span className="text-xs sm:text-sm font-semibold text-[#0D2B4E] tracking-wide">Unser Einzugsgebiet</span>
            </div>
            <h2 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold text-gray-900 mb-3 sm:mb-4">Ratingen & Umgebung</h2>
            <p className="text-base sm:text-lg md:text-xl text-gray-600 max-w-3xl mx-auto px-4">
              Wir sind Ihr zuverlässiger Partner für professionelle Gebäudedienstleistungen in <strong>Ratingen</strong> und der gesamten Region. Schnelle Anfahrt, lokale Expertise.
            </p>
          </div>

          <div className="grid lg:grid-cols-2 gap-8 sm:gap-12 items-center max-w-6xl mx-auto">
            {/* Map */}
            <div className="order-2 lg:order-1">
              <div className="relative rounded-2xl overflow-hidden shadow-2xl border-4 border-[#0D2B4E]/10">
                <div className="aspect-[4/3] w-full">
                  <iframe
                    src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d40486.89!2d6.8499!3d51.2966!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x47b8c9c0c0c0c0c0%3A0x0!2sRatingen!5e0!3m2!1sde!2sde!4v1234567890"
                    width="100%"
                    height="100%"
                    style={{ border: 0 }}
                    allowFullScreen
                    loading="lazy"
                    referrerPolicy="no-referrer-when-downgrade"
                    title="IGM Facility Service Einzugsgebiet Ratingen"
                  ></iframe>
                </div>
                <div className="absolute top-4 left-4 bg-white/95 backdrop-blur-sm rounded-lg px-4 py-2 shadow-lg">
                  <div className="flex items-center gap-2">
                    <div className="w-3 h-3 bg-[#0D2B4E] rounded-full animate-pulse"></div>
                    <span className="text-sm font-bold text-gray-900">Ratingen 40878</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Cities List */}
            <div className="order-1 lg:order-2">
              <div className="bg-gray-50 rounded-2xl p-6 sm:p-8 border border-gray-200">
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-12 h-12 bg-[#0D2B4E] rounded-lg flex items-center justify-center flex-shrink-0">
                    <i className="ri-map-2-line text-2xl text-white"></i>
                  </div>
                  <div>
                    <h3 className="text-xl sm:text-2xl font-bold text-gray-900">Wir sind für Sie da</h3>
                    <p className="text-sm text-gray-600">In diesen Städten im Einsatz</p>
                  </div>
                </div>

                <div className="flex flex-wrap gap-2 sm:gap-3 mb-6">
                  {[
                    'Ratingen',
                    'Düsseldorf',
                    'Mettmann',
                    'Erkrath',
                    'Hilden',
                    'Langenfeld',
                    'Velbert',
                    'Heiligenhaus',
                    'Wülfrath',
                    'Neuss',
                    'Krefeld',
                  ].map((city, i) => (
                    <div
                      key={i}
                      className={`px-4 py-2 rounded-lg font-semibold text-sm transition-all cursor-default ${
                        city === 'Ratingen'
                          ? 'bg-[#0D2B4E] text-white shadow-md'
                          : 'bg-white text-gray-700 border border-gray-300 hover:border-[#0D2B4E] hover:text-[#0D2B4E]'
                      }`}
                    >
                      {city === 'Ratingen' && <i className="ri-home-heart-fill mr-1"></i>}
                      {city}
                    </div>
                  ))}
                </div>

                <div className="space-y-3 mb-6">
                  <div className="flex items-start gap-3">
                    <div className="w-6 h-6 bg-[#0D2B4E]/10 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                      <i className="ri-check-line text-[#0D2B4E] text-sm"></i>
                    </div>
                    <p className="text-sm text-gray-700">
                      <strong>Schnelle Anfahrt:</strong> Innerhalb von 30 Minuten bei Ihnen vor Ort
                    </p>
                  </div>
                  <div className="flex items-start gap-3">
                    <div className="w-6 h-6 bg-[#0D2B4E]/10 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                      <i className="ri-check-line text-[#0D2B4E] text-sm"></i>
                    </div>
                    <p className="text-sm text-gray-700">
                      <strong>Lokale Expertise:</strong> Wir kennen die Region und ihre Anforderungen
                    </p>
                  </div>
                  <div className="flex items-start gap-3">
                    <div className="w-6 h-6 bg-[#0D2B4E]/10 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                      <i className="ri-check-line text-[#0D2B4E] text-sm"></i>
                    </div>
                    <p className="text-sm text-gray-700">
                      <strong>Flexible Einsatzzeiten:</strong> 24/7 Notdienst für Schlüsseldienst verfügbar
                    </p>
                  </div>
                </div>

                <Link
                  to="/kontakt"
                  className="inline-flex items-center justify-center gap-2 bg-[#0D2B4E] text-white px-6 py-3 rounded-lg font-semibold hover:bg-[#0a2240] transition-colors w-full cursor-pointer whitespace-nowrap"
                >
                  <i className="ri-phone-line text-lg"></i>
                  <span>Jetzt anfragen</span>
                </Link>
              </div>
            </div>
          </div>

          <div className="text-center mt-10 sm:mt-12">
            <p className="text-gray-500 text-sm">
              Ihre Stadt ist nicht dabei? <a href="/kontakt" className="text-[#0D2B4E] font-semibold hover:underline cursor-pointer">Kontaktieren Sie uns</a> – wir prüfen gerne, ob wir auch in Ihrer Region tätig werden können.
            </p>
          </div>
        </div>
      </section>

      {/* CTA Banner Section */}
      <section className="py-12 sm:py-20 bg-[#0D2B4E] relative overflow-hidden">
        <div className="absolute inset-0 opacity-5">
          <div className="absolute top-0 left-0 w-96 h-96 bg-white rounded-full -translate-x-1/2 -translate-y-1/2"></div>
          <div className="absolute bottom-0 right-0 w-96 h-96 bg-white rounded-full translate-x-1/2 translate-y-1/2"></div>
        </div>
        <div className="container mx-auto px-4 sm:px-6 relative z-10">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold text-white mb-4 sm:mb-6">Jetzt kostenloses Angebot anfordern</h2>
            <p className="text-base sm:text-lg md:text-xl text-white/80 mb-6 sm:mb-10 leading-relaxed px-4">
              Kontaktieren Sie uns noch heute und erhalten Sie ein unverbindliches, maßgeschneidertes Angebot für Ihre Bedürfnisse.
            </p>
            <div className="flex flex-col gap-3 sm:gap-4 justify-center">
              <Link to="/kontakt" className="inline-flex items-center justify-center gap-2 bg-white text-[#0D2B4E] px-6 sm:px-8 py-3 sm:py-4 rounded-lg text-base sm:text-lg font-semibold hover:bg-gray-100 transition-all shadow-lg whitespace-nowrap cursor-pointer w-full sm:w-auto">
                <i className="ri-mail-line text-xl"></i>
                <span>Angebot anfragen</span>
              </Link>
              <a href="tel:+492102739807" className="inline-flex items-center justify-center gap-2 bg-white/10 border border-white/30 text-white px-6 sm:px-8 py-3 sm:py-4 rounded-lg text-base sm:text-lg font-semibold hover:bg-white/20 transition-all shadow-lg whitespace-nowrap cursor-pointer w-full sm:w-auto">
                <i className="ri-phone-line text-xl"></i>
                <span>02102 / 7398070</span>
              </a>
            </div>
          </div>
        </div>
      </section>

      <Footer />

      {/* Back to Top Button */}
      <button
        onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
        className={`fixed bottom-4 right-4 sm:bottom-8 sm:right-8 w-12 h-12 sm:w-14 sm:h-14 bg-[#0D2B4E] text-white rounded-full shadow-lg hover:bg-[#0a2240] transition-all flex items-center justify-center z-40 cursor-pointer ${isScrolled ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4 pointer-events-none'}`}
      >
        <i className="ri-arrow-up-line text-xl sm:text-2xl"></i>
      </button>
    </div>
  );
}